import { getDB } from "features/medical-scribe/medical-scribe-db/medical-scribe-db.util";

/**
 * The copy of a recording this browser kept in IndexedDB while it recorded (SN-19937).
 *
 * Copied, not moved, from `useScribeAudioDownload`, which stays untouched while
 * AMBIENT_SCRIBE_SERVER_AUDIO_DOWNLOAD is off. Delete that hook and this note together when the
 * flag graduates.
 */

type ScribeDB = Awaited<ReturnType<typeof getDB>>;

export type LocalRecording = {
  recordingId: string;
  metadata: any;
  chunks: Blob[];
};

/**
 * The first local copy of this scribe that holds audio. A scribe resumed after a page reload has a
 * second local row, and the first can be empty, so an empty match is skipped rather than returned.
 */
export async function findLocalRecordingWithAudio(
  db: ScribeDB,
  remoteMedicalScribeId: number | undefined,
  localId: string | undefined
): Promise<LocalRecording | null> {
  const candidates: { recordingId: string; metadata: any }[] = [];
  if (remoteMedicalScribeId) {
    const allRecordings = await db
      .transaction("recordings", "readonly")
      .objectStore("recordings")
      .getAll();
    for (const recording of allRecordings) {
      if (recording.medical_transcribe_id === remoteMedicalScribeId) {
        candidates.push({ recordingId: recording.id, metadata: recording });
      }
    }
  } else if (localId) {
    candidates.push({
      recordingId: localId,
      metadata: await db.get("recordings", localId)
    });
  }

  for (const candidate of candidates) {
    const chunksRecord = await db.get(
      "recordings_chunks",
      candidate.recordingId
    );
    if (chunksRecord?.chunks?.length) {
      return { ...candidate, chunks: chunksRecord.chunks };
    }
  }
  return null;
}

/** One playable file from the saved chunks: WAV for a streamed recording, WebM otherwise. */
export async function buildLocalRecordingFile(
  recording: LocalRecording
): Promise<{ blob: Blob; extension: "wav" | "webm" }> {
  if (!(recording.metadata?.isStreaming ?? false)) {
    return {
      blob: new Blob(recording.chunks, { type: "audio/webm" }),
      extension: "webm"
    };
  }

  // Streaming mode: each chunk is a complete WAV file — extract PCM data and combine
  const chunkBuffers = await Promise.all(
    recording.chunks.map((chunk) => chunk.arrayBuffer())
  );
  // Skip each chunk's 44-byte WAV header and keep its PCM data
  const pcmDataArrays = chunkBuffers.map((buffer) => buffer.slice(44));
  const totalPcmSize = pcmDataArrays.reduce(
    (size, pcmData) => size + pcmData.byteLength,
    0
  );

  // Build a single WAV header for the combined PCM stream
  const wavHeader = new ArrayBuffer(44);
  const view = new DataView(wavHeader);
  const sampleRate = 16000;
  const numChannels = 1;
  const bitsPerSample = 16;
  const blockAlign = numChannels * (bitsPerSample / 8);
  const byteRate = sampleRate * blockAlign;
  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.codePointAt(i) || 0);
    }
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + totalPcmSize, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  writeString(36, "data");
  view.setUint32(40, totalPcmSize, true);

  return {
    blob: new Blob([wavHeader, ...pcmDataArrays], { type: "audio/wav" }),
    extension: "wav"
  };
}
